// src/contexts/FileManagerContext.tsx
import React, { createContext, useReducer, useContext, ReactNode, useCallback } from 'react';
import {
  Folder,
  FolderContents,
  Breadcrumb,
  StorageQuota,
  SharedFolder,
  TrashContents,
  FileManagerSection,
  ViewMode,
  SortBy,
  SortOrder,
  SelectedItem,
  FileDocument,
  SearchResults,
} from '../types/files';
import * as fileService from '../services/fileService';

// Favorites contents
interface FavoritesContents {
  folders: Folder[];
  documents: FileDocument[];
}

// State interface
interface FileManagerState {
  currentFolder: number | null;
  section: FileManagerSection;
  previousSection: FileManagerSection;  // To restore after search
  contents: FolderContents | null;
  breadcrumb: Breadcrumb[];
  selectedItems: SelectedItem[];
  viewMode: ViewMode;
  sortBy: SortBy;
  sortOrder: SortOrder;
  loading: boolean;
  uploading: boolean;
  uploadProgress: number;
  quota: StorageQuota | null;
  sharedWithMe: SharedFolder[];
  trash: TrashContents | null;
  favorites: FavoritesContents | null;
  recentDocuments: FileDocument[];
  error: string | null;
  folderTree: Folder[];
  // Search state
  searchQuery: string;
  searchResults: SearchResults | null;
  searching: boolean;
}

// Actions
type FileManagerAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SECTION'; payload: FileManagerSection }
  | { type: 'SET_CURRENT_FOLDER'; payload: number | null }
  | { type: 'SET_CONTENTS'; payload: FolderContents | null }
  | { type: 'SET_BREADCRUMB'; payload: Breadcrumb[] }
  | { type: 'SET_SELECTED_ITEMS'; payload: SelectedItem[] }
  | { type: 'ADD_SELECTED_ITEM'; payload: SelectedItem }
  | { type: 'REMOVE_SELECTED_ITEM'; payload: SelectedItem }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SET_VIEW_MODE'; payload: ViewMode }
  | { type: 'SET_SORT'; payload: { sortBy: SortBy; sortOrder: SortOrder } }
  | { type: 'SET_QUOTA'; payload: StorageQuota | null }
  | { type: 'SET_SHARED_WITH_ME'; payload: SharedFolder[] }
  | { type: 'SET_TRASH'; payload: TrashContents | null }
  | { type: 'SET_FAVORITES'; payload: FavoritesContents | null }
  | { type: 'SET_RECENT_DOCUMENTS'; payload: FileDocument[] }
  | { type: 'SET_UPLOADING'; payload: { uploading: boolean; progress: number } }
  | { type: 'SET_FOLDER_TREE'; payload: Folder[] }
  | { type: 'ADD_FOLDER'; payload: Folder }
  | { type: 'UPDATE_FOLDER'; payload: Folder }
  | { type: 'REMOVE_FOLDER'; payload: number }
  | { type: 'ADD_DOCUMENT'; payload: FileDocument }
  | { type: 'REMOVE_DOCUMENT'; payload: number }
  | { type: 'SET_SEARCHING'; payload: boolean }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_SEARCH_RESULTS'; payload: SearchResults | null }
  | { type: 'ENTER_SEARCH_MODE'; payload: { query: string; previousSection: FileManagerSection } }
  | { type: 'EXIT_SEARCH_MODE' };

const initialState: FileManagerState = {
  currentFolder: null,
  section: 'my-space',
  previousSection: 'my-space',
  contents: null,
  breadcrumb: [],
  selectedItems: [],
  viewMode: 'grid',
  sortBy: 'name',
  sortOrder: 'asc',
  loading: false,
  uploading: false,
  uploadProgress: 0,
  quota: null,
  sharedWithMe: [],
  trash: null,
  favorites: null,
  recentDocuments: [],
  error: null,
  folderTree: [],
  // Search
  searchQuery: '',
  searchResults: null,
  searching: false,
};

function fileManagerReducer(state: FileManagerState, action: FileManagerAction): FileManagerState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_SECTION':
      return { ...state, section: action.payload, selectedItems: [] };
    case 'SET_CURRENT_FOLDER':
      return { ...state, currentFolder: action.payload, selectedItems: [] };
    case 'SET_CONTENTS':
      return { ...state, contents: action.payload };
    case 'SET_BREADCRUMB':
      return { ...state, breadcrumb: action.payload };
    case 'SET_SELECTED_ITEMS':
      return { ...state, selectedItems: action.payload };
    case 'ADD_SELECTED_ITEM':
      if (state.selectedItems.some(item => item.id === action.payload.id && item.type === action.payload.type)) {
        return state;
      }
      return { ...state, selectedItems: [...state.selectedItems, action.payload] };
    case 'REMOVE_SELECTED_ITEM':
      return {
        ...state,
        selectedItems: state.selectedItems.filter(
          item => !(item.id === action.payload.id && item.type === action.payload.type)
        ),
      };
    case 'CLEAR_SELECTION':
      return { ...state, selectedItems: [] };
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };
    case 'SET_SORT':
      return { ...state, sortBy: action.payload.sortBy, sortOrder: action.payload.sortOrder };
    case 'SET_QUOTA':
      return { ...state, quota: action.payload };
    case 'SET_SHARED_WITH_ME':
      return { ...state, sharedWithMe: action.payload };
    case 'SET_TRASH':
      return { ...state, trash: action.payload };
    case 'SET_FAVORITES':
      return { ...state, favorites: action.payload };
    case 'SET_RECENT_DOCUMENTS':
      return { ...state, recentDocuments: action.payload };
    case 'SET_UPLOADING':
      return { ...state, uploading: action.payload.uploading, uploadProgress: action.payload.progress };
    case 'SET_FOLDER_TREE':
      return { ...state, folderTree: action.payload };
    case 'ADD_FOLDER':
      if (!state.contents) return state;
      return {
        ...state,
        contents: {
          ...state.contents,
          folders: [...state.contents.folders, action.payload],
          total_folders: state.contents.total_folders + 1,
        },
      };
    case 'UPDATE_FOLDER':
      if (!state.contents) return state;
      return {
        ...state,
        contents: {
          ...state.contents,
          folders: state.contents.folders.map(f => (f.id === action.payload.id ? action.payload : f)),
        },
      };
    case 'REMOVE_FOLDER':
      if (!state.contents) return state;
      return {
        ...state,
        contents: {
          ...state.contents,
          folders: state.contents.folders.filter(f => f.id !== action.payload),
          total_folders: state.contents.total_folders - 1,
        },
      };
    case 'ADD_DOCUMENT':
      if (!state.contents) return state;
      return {
        ...state,
        contents: {
          ...state.contents,
          documents: [...state.contents.documents, action.payload],
          total_documents: state.contents.total_documents + 1,
        },
      };
    case 'REMOVE_DOCUMENT':
      if (!state.contents) return state;
      return {
        ...state,
        contents: {
          ...state.contents,
          documents: state.contents.documents.filter(d => d.id !== action.payload),
          total_documents: state.contents.total_documents - 1,
        },
      };
    case 'SET_SEARCHING':
      return { ...state, searching: action.payload };
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    case 'SET_SEARCH_RESULTS':
      return { ...state, searchResults: action.payload };
    case 'ENTER_SEARCH_MODE':
      return {
        ...state,
        section: 'search',
        previousSection: action.payload.previousSection,
        searchQuery: action.payload.query,
        selectedItems: [],
      };
    case 'EXIT_SEARCH_MODE':
      return {
        ...state,
        section: state.previousSection,
        searchQuery: '',
        searchResults: null,
        searching: false,
        selectedItems: [],
      };
    default:
      return state;
  }
}

// Context type
interface FileManagerContextType {
  state: FileManagerState;
  // Navigation
  navigateToFolder: (folderId: number | null) => Promise<void>;
  navigateToSection: (section: FileManagerSection) => Promise<void>;
  refreshContents: () => Promise<void>;
  // Selection
  selectItem: (item: SelectedItem, ctrlKey?: boolean, shiftKey?: boolean) => void;
  clearSelection: () => void;
  isSelected: (id: number, type: 'folder' | 'document') => boolean;
  // View
  setViewMode: (mode: ViewMode) => void;
  setSort: (sortBy: SortBy, sortOrder: SortOrder) => void;
  // Folder operations
  createFolder: (name: string, parentId?: number | null) => Promise<Folder>;
  renameFolder: (folderId: number, newName: string) => Promise<void>;
  deleteFolder: (folderId: number, permanent?: boolean) => Promise<void>;
  moveFolder: (folderId: number, newParentId: number | null) => Promise<void>;
  // Document operations
  uploadDocument: (file: File) => Promise<FileDocument>;
  deleteDocument: (documentId: number) => Promise<void>;
  downloadDocument: (documentId: number, filename: string) => Promise<void>;
  // Trash operations
  restoreItem: (itemType: 'folder' | 'document', itemId: number) => Promise<void>;
  permanentDeleteItem: (itemType: 'folder' | 'document', itemId: number) => Promise<void>;
  emptyTrash: () => Promise<void>;
  // Favorites
  toggleFavorite: (itemId: number, isFavorite: boolean, itemType: 'folder' | 'document') => Promise<void>;
  // Quota
  loadQuota: () => Promise<void>;
  // Search
  search: (query: string) => Promise<void>;
  clearSearch: () => void;
  navigateToSearchResult: (itemType: 'folder' | 'document', itemId: number, parentFolderId: number | null) => Promise<void>;
}

const FileManagerContext = createContext<FileManagerContextType | undefined>(undefined);

// Provider
interface FileManagerProviderProps {
  children: ReactNode;
}

export const FileManagerProvider: React.FC<FileManagerProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(fileManagerReducer, initialState);

  // Navigation
  const navigateToFolder = useCallback(async (folderId: number | null) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    // Always switch to my-space section when navigating to a folder
    dispatch({ type: 'SET_SECTION', payload: 'my-space' });
    try {
      dispatch({ type: 'SET_CURRENT_FOLDER', payload: folderId });

      if (folderId === null) {
        // Root folder
        const contents = await fileService.getRootContents();
        dispatch({ type: 'SET_CONTENTS', payload: contents });
        dispatch({ type: 'SET_BREADCRUMB', payload: [] });
      } else {
        const [contents, breadcrumb] = await Promise.all([
          fileService.getFolderContents(folderId),
          fileService.getBreadcrumb(folderId),
        ]);
        dispatch({ type: 'SET_CONTENTS', payload: contents });
        dispatch({ type: 'SET_BREADCRUMB', payload: breadcrumb });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.response?.data?.detail || 'Failed to load folder contents' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const navigateToSection = useCallback(async (section: FileManagerSection) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_SECTION', payload: section });

    try {
      switch (section) {
        case 'my-space':
          dispatch({ type: 'SET_CURRENT_FOLDER', payload: null });
          const contents = await fileService.getRootContents();
          dispatch({ type: 'SET_CONTENTS', payload: contents });
          dispatch({ type: 'SET_BREADCRUMB', payload: [] });
          break;
        case 'shared':
          const shared = await fileService.getSharedWithMe();
          dispatch({ type: 'SET_SHARED_WITH_ME', payload: shared });
          dispatch({ type: 'SET_CONTENTS', payload: null });
          break;
        case 'trash':
          const trash = await fileService.getTrashContents();
          dispatch({ type: 'SET_TRASH', payload: trash });
          dispatch({ type: 'SET_CONTENTS', payload: null });
          break;
        case 'favorites':
          const [favFolders, favDocs] = await Promise.all([
            fileService.getFavoriteFolders(),
            fileService.getFavoriteDocuments(),
          ]);
          dispatch({ type: 'SET_FAVORITES', payload: { folders: favFolders, documents: favDocs } });
          dispatch({ type: 'SET_CONTENTS', payload: null });
          break;
        case 'recent':
          const recentDocs = await fileService.getRecentDocuments(50);
          dispatch({ type: 'SET_RECENT_DOCUMENTS', payload: recentDocs });
          dispatch({ type: 'SET_CONTENTS', payload: null });
          break;
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.response?.data?.detail || 'Failed to load section' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const refreshContents = useCallback(async () => {
    if (state.section === 'my-space') {
      await navigateToFolder(state.currentFolder);
    } else {
      await navigateToSection(state.section);
    }
  }, [state.section, state.currentFolder, navigateToFolder, navigateToSection]);

  // Selection
  const selectItem = useCallback((item: SelectedItem, ctrlKey = false, shiftKey = false) => {
    if (ctrlKey) {
      // Toggle selection
      const isCurrentlySelected = state.selectedItems.some(
        s => s.id === item.id && s.type === item.type
      );
      if (isCurrentlySelected) {
        dispatch({ type: 'REMOVE_SELECTED_ITEM', payload: item });
      } else {
        dispatch({ type: 'ADD_SELECTED_ITEM', payload: item });
      }
    } else if (shiftKey) {
      // Range selection (simplified - just adds to selection)
      dispatch({ type: 'ADD_SELECTED_ITEM', payload: item });
    } else {
      // Single selection
      dispatch({ type: 'SET_SELECTED_ITEMS', payload: [item] });
    }
  }, [state.selectedItems]);

  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, []);

  const isSelected = useCallback((id: number, type: 'folder' | 'document') => {
    return state.selectedItems.some(item => item.id === id && item.type === type);
  }, [state.selectedItems]);

  // View
  const setViewMode = useCallback((mode: ViewMode) => {
    dispatch({ type: 'SET_VIEW_MODE', payload: mode });
    localStorage.setItem('fileManager_viewMode', mode);
  }, []);

  const setSort = useCallback((sortBy: SortBy, sortOrder: SortOrder) => {
    dispatch({ type: 'SET_SORT', payload: { sortBy, sortOrder } });
    localStorage.setItem('fileManager_sortBy', sortBy);
    localStorage.setItem('fileManager_sortOrder', sortOrder);
  }, []);

  // Folder operations
  const createFolder = useCallback(async (name: string, parentId?: number | null) => {
    const folder = await fileService.createFolder({
      name,
      parent_id: parentId ?? state.currentFolder,
    });
    dispatch({ type: 'ADD_FOLDER', payload: folder });
    return folder;
  }, [state.currentFolder]);

  const renameFolder = useCallback(async (folderId: number, newName: string) => {
    const folder = await fileService.updateFolder(folderId, { name: newName });
    dispatch({ type: 'UPDATE_FOLDER', payload: folder });
  }, []);

  const deleteFolder = useCallback(async (folderId: number, permanent = false) => {
    await fileService.deleteFolder(folderId, permanent);
    dispatch({ type: 'REMOVE_FOLDER', payload: folderId });
  }, []);

  const moveFolder = useCallback(async (folderId: number, newParentId: number | null) => {
    await fileService.moveFolder(folderId, newParentId);
    dispatch({ type: 'REMOVE_FOLDER', payload: folderId });
  }, []);

  // Document operations
  const uploadDocument = useCallback(async (file: File) => {
    dispatch({ type: 'SET_UPLOADING', payload: { uploading: true, progress: 0 } });
    try {
      const document = await fileService.uploadDocument(file, state.currentFolder, (progress) => {
        dispatch({ type: 'SET_UPLOADING', payload: { uploading: true, progress } });
      });
      dispatch({ type: 'ADD_DOCUMENT', payload: document });
      return document;
    } finally {
      dispatch({ type: 'SET_UPLOADING', payload: { uploading: false, progress: 0 } });
    }
  }, [state.currentFolder]);

  const deleteDocument = useCallback(async (documentId: number) => {
    await fileService.deleteDocument(documentId);
    dispatch({ type: 'REMOVE_DOCUMENT', payload: documentId });
  }, []);

  const downloadDocument = useCallback(async (documentId: number, filename: string) => {
    const blob = await fileService.downloadDocument(documentId);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  // Trash operations
  const restoreItem = useCallback(async (itemType: 'folder' | 'document', itemId: number) => {
    await fileService.restoreFromTrash(itemType, itemId);
    await refreshContents();
  }, [refreshContents]);

  const permanentDeleteItem = useCallback(async (itemType: 'folder' | 'document', itemId: number) => {
    await fileService.permanentDelete(itemType, itemId);
    await refreshContents();
  }, [refreshContents]);

  const emptyTrashHandler = useCallback(async () => {
    await fileService.emptyTrash();
    dispatch({ type: 'SET_TRASH', payload: { items: [], total_count: 0, total_size: 0, days_until_auto_delete: 30 } });
  }, []);

  // Favorites
  const toggleFavorite = useCallback(async (itemId: number, isFavorite: boolean, itemType: 'folder' | 'document') => {
    if (itemType === 'folder') {
      if (isFavorite) {
        await fileService.removeFolderFromFavorites(itemId);
      } else {
        await fileService.addFolderToFavorites(itemId);
      }
      // Update local state for folders
      if (state.contents) {
        const updatedFolders = state.contents.folders.map(f =>
          f.id === itemId ? { ...f, is_favorite: !isFavorite } : f
        );
        dispatch({
          type: 'SET_CONTENTS',
          payload: { ...state.contents, folders: updatedFolders },
        });
      }
    } else {
      // Documents - use document favorite API
      if (isFavorite) {
        await fileService.removeDocumentFromFavorites(itemId);
      } else {
        await fileService.addDocumentToFavorites(itemId);
      }
      // Update local state for documents
      if (state.contents) {
        const updatedDocuments = state.contents.documents.map(d =>
          d.id === itemId ? { ...d, is_favorite: !isFavorite } : d
        );
        dispatch({
          type: 'SET_CONTENTS',
          payload: { ...state.contents, documents: updatedDocuments },
        });
      }
    }
  }, [state.contents]);

  // Quota
  const loadQuota = useCallback(async () => {
    try {
      const quota = await fileService.getStorageQuota();
      dispatch({ type: 'SET_QUOTA', payload: quota });
    } catch (error) {
      console.error('Failed to load quota:', error);
    }
  }, []);

  // Search
  const search = useCallback(async (query: string) => {
    if (query.length < 2) {
      // Clear search if query is too short
      if (state.section === 'search') {
        dispatch({ type: 'EXIT_SEARCH_MODE' });
      }
      return;
    }

    // Enter search mode if not already
    if (state.section !== 'search') {
      dispatch({
        type: 'ENTER_SEARCH_MODE',
        payload: { query, previousSection: state.section },
      });
    } else {
      dispatch({ type: 'SET_SEARCH_QUERY', payload: query });
    }

    dispatch({ type: 'SET_SEARCHING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const results = await fileService.globalSearch(query);
      dispatch({ type: 'SET_SEARCH_RESULTS', payload: results });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.response?.data?.detail || 'Search failed' });
    } finally {
      dispatch({ type: 'SET_SEARCHING', payload: false });
    }
  }, [state.section]);

  const clearSearch = useCallback(() => {
    dispatch({ type: 'EXIT_SEARCH_MODE' });
    // Refresh the previous section content
    navigateToSection(state.previousSection);
  }, [state.previousSection, navigateToSection]);

  const navigateToSearchResult = useCallback(async (
    itemType: 'folder' | 'document',
    itemId: number,
    parentFolderId: number | null
  ) => {
    // Clear search first
    dispatch({ type: 'EXIT_SEARCH_MODE' });

    if (itemType === 'folder') {
      // Navigate to the folder
      await navigateToFolder(itemId);
    } else {
      // Navigate to the parent folder and select the document
      await navigateToFolder(parentFolderId);
      // Select the document after navigation
      dispatch({
        type: 'SET_SELECTED_ITEMS',
        payload: [{ id: itemId, type: 'document' }],
      });
    }
  }, [navigateToFolder]);

  const value: FileManagerContextType = {
    state,
    navigateToFolder,
    navigateToSection,
    refreshContents,
    selectItem,
    clearSelection,
    isSelected,
    setViewMode,
    setSort,
    createFolder,
    renameFolder,
    deleteFolder,
    moveFolder,
    uploadDocument,
    deleteDocument,
    downloadDocument,
    restoreItem,
    permanentDeleteItem,
    emptyTrash: emptyTrashHandler,
    toggleFavorite,
    loadQuota,
    search,
    clearSearch,
    navigateToSearchResult,
  };

  return (
    <FileManagerContext.Provider value={value}>
      {children}
    </FileManagerContext.Provider>
  );
};

// Hook
export const useFileManager = (): FileManagerContextType => {
  const context = useContext(FileManagerContext);
  if (context === undefined) {
    throw new Error('useFileManager must be used within a FileManagerProvider');
  }
  return context;
};

export default FileManagerContext;
